/**
 * SpeedometerStore — caches speedometer sensitivity API responses per company.
 *
 * Prevents redundant API calls when switching tabs and returning to Sensitivity.
 * Stores the full response + selected year so the UI restores instantly.
 */

import { create } from 'zustand';
import type { SpeedometerSensitivityResponse } from '../types/financial';

interface CachedEntry {
  data: SpeedometerSensitivityResponse;
  selectedYear: number | string;
  /** Slider values that produced this cached response */
  sliders: { revenue: number; ebitda: number; investedCapital: number; wacc: number };
}

interface SpeedometerState {
  cache: Record<string, CachedEntry>;

  getCached: (company: string) => CachedEntry | null;
  setCache: (company: string, entry: CachedEntry) => void;
  clearCache: (company: string) => void;
}

export const useSpeedometerStore = create<SpeedometerState>((set, get) => ({
  cache: {},

  getCached: (company) => get().cache[company] ?? null,

  setCache: (company, entry) =>
    set((state) => ({
      cache: { ...state.cache, [company]: entry },
    })),

  clearCache: (company) =>
    set((state) => {
      const { [company]: _, ...rest } = state.cache;
      return { cache: rest };
    }),
}));
